import { defineMessages } from 'react-intl';

import type { AgentPromptPresetList } from '../config';

import { commonPresets as c } from './common';

// Preset sets per report page flavor. Texts respect each modality's limits
// (what the view can and cannot show) and keep clinical decisions with the
// clinician.
export const reportMessages = defineMessages({
  reportWalkthroughTitle: {
    id: 'agentPrompts.report.walkthroughTitle',
    defaultMessage: 'Explain this report',
  },
  reportWalkthroughPrompt: {
    id: 'agentPrompts.report.walkthroughPrompt',
    defaultMessage:
      'Walk me through this report.\n\nOutput:\n1. What kind of study this is and what this view can and cannot show\n2. The key findings, tooth by tooth, in clinical but readable language\n3. Which findings matter most and why\n4. What the clinician should verify chairside\n\nBase every statement on the report data and note the modality limits where relevant. Answer in the chat; do not create a patient document.',
  },
  cbctWalkthroughTitle: {
    id: 'agentPrompts.reportCbct.walkthroughTitle',
    defaultMessage: 'Explain this report',
  },
  cbctWalkthroughPrompt: {
    id: 'agentPrompts.reportCbct.walkthroughPrompt',
    defaultMessage:
      'Walk me through this CBCT report.\n\nOutput:\n1. Study basics (type, date, limitations such as metal artifacts)\n2. Dentoalveolar findings, tooth by tooth\n3. Jaw bone findings (lesions, bone quality)\n4. Mandibular canal and nerve position relative to teeth or planned sites\n5. TMJ, sinuses, and airway observations\n6. Incidental findings worth clinician review\n\nCBCT cannot assess soft tissue — say so where it matters. Answer in the chat; do not create a patient document.',
  },
  cbctIncidentalTitle: {
    id: 'agentPrompts.reportCbct.incidentalTitle',
    defaultMessage: 'Incidental findings review',
  },
  cbctIncidentalPrompt: {
    id: 'agentPrompts.reportCbct.incidentalPrompt',
    defaultMessage:
      'Review this CBCT report for incidental findings outside the area of clinical interest.\n\nOutput:\n1. Paranasal sinuses: mucosal thickening, opacification, sinus floor issues\n2. TMJ: condyle morphology, flattening, erosions\n3. Airway: constrictions or notable narrowing\n4. Jaws: cysts, tumors, or bone pathology away from the focus area\n5. Soft-tissue calcifications noted in the report\n6. Which of these warrant referral or follow-up, and to whom\n\nBase the review strictly on the report data. Flag findings for clinician review; do not diagnose. Answer in the chat; do not create a patient document.',
  },
  panoWalkthroughTitle: {
    id: 'agentPrompts.reportPano.walkthroughTitle',
    defaultMessage: 'Explain this report',
  },
  panoWalkthroughPrompt: {
    id: 'agentPrompts.reportPano.walkthroughPrompt',
    defaultMessage:
      'Walk me through this panoramic report.\n\nOutput:\n1. Study basics and date\n2. Teeth present, missing, and restored, with tooth numbers\n3. Third molars: presence, position, impaction signs\n4. Jaw and pathology observations (cysts, bone lesions, sinus floors, mandibular canal)\n5. What a panoramic view cannot show reliably (fine caries detail, exact measurements) and what to verify with other imaging\n\nAnswer in the chat; do not create a patient document.',
  },
  panoThirdMolarsTitle: {
    id: 'agentPrompts.reportPano.thirdMolarsTitle',
    defaultMessage: 'Third molar overview',
  },
  panoThirdMolarsPrompt: {
    id: 'agentPrompts.reportPano.thirdMolarsPrompt',
    defaultMessage:
      'Summarize what this panoramic report shows about the third molars.\n\nOutput:\n1. Which third molars are present, with tooth numbers\n2. Position and eruption status of each\n3. Any proximity signs to the mandibular canal or the second molar\n4. Associated findings: pericoronitis signs, caries, cystic follicle, resorption\n5. What the clinician should assess before any decision\n\nPresent the findings only; the monitor-versus-remove decision stays with the clinician. Answer in the chat; do not create a patient document.',
  },
  bitewingsCariesTitle: {
    id: 'agentPrompts.reportBitewings.cariesTitle',
    defaultMessage: 'Caries findings',
  },
  bitewingsCariesPrompt: {
    id: 'agentPrompts.reportBitewings.cariesPrompt',
    defaultMessage:
      'Summarize the caries findings in this bitewing report.\n\nOutput:\n1. Teeth with caries, by tooth number and surface, ordered by depth (enamel, outer dentin, deep)\n2. New caries versus progression of known lesions, if a previous study exists\n3. Teeth to monitor, with what change would matter\n4. What bitewings cannot show (apices, buccal or lingual surfaces, furcation) and what needs another view\n\nBase every statement on the report. Answer in the chat; do not create a patient document.',
  },
  bitewingsRestorationsTitle: {
    id: 'agentPrompts.reportBitewings.restorationsTitle',
    defaultMessage: 'Restoration review',
  },
  bitewingsRestorationsPrompt: {
    id: 'agentPrompts.reportBitewings.restorationsPrompt',
    defaultMessage:
      'Review the existing restorations and their condition in this bitewing report.\n\nOutput:\n1. Restorations visible, by tooth number and type\n2. Restorations with defects, overhangs, or recurrent caries signs\n3. Margin quality concerns worth clinical verification\n4. Restorative needs this suggests, as flags — not a final treatment plan\n\nAnswer in the chat; do not create a patient document.',
  },
  ioxrayPeriapicalTitle: {
    id: 'agentPrompts.reportIoxray.periapicalTitle',
    defaultMessage: 'Periapical status',
  },
  ioxrayPeriapicalPrompt: {
    id: 'agentPrompts.reportIoxray.periapicalPrompt',
    defaultMessage:
      'Summarize the periapical status in this intraoral X-ray report.\n\nOutput:\n1. Periapical findings by tooth number: radiolucencies, widened ligament space, bone changes\n2. Root and canal observations: length, curvature, calcifications, prior treatment\n3. Distinctions the report makes between true lesions and anatomical superimposition\n4. What needs pulp testing or another view to confirm\n\nAnswer in the chat; do not create a patient document.',
  },
  ioxrayEndoConcernsTitle: {
    id: 'agentPrompts.reportIoxray.endoConcernsTitle',
    defaultMessage: 'Endodontic concerns',
  },
  ioxrayEndoConcernsPrompt: {
    id: 'agentPrompts.reportIoxray.endoConcernsPrompt',
    defaultMessage:
      'List the endodontic concerns raised by this intraoral X-ray report.\n\nOutput:\n1. Teeth with periapical or pulpal findings that suggest endodontic attention\n2. Prior root canal treatments: fill length, density, gaps, missed anatomy\n3. Resorption or fracture signs the report notes\n4. Restorability signals: caries depth, margin-to-pulp distance\n5. What to verify chairside (pulp testing, angulated views) before treatment decisions\n\nFlags only — the endodontic decision stays with the clinician. Answer in the chat; do not create a patient document.',
  },
  endoAssessmentTitle: {
    id: 'agentPrompts.reportEndo.assessmentTitle',
    defaultMessage: 'Endodontic assessment',
  },
  endoAssessmentPrompt: {
    id: 'agentPrompts.reportEndo.assessmentPrompt',
    defaultMessage:
      'Summarize this endodontic CBCT report for treatment planning.\n\nOutput:\n1. Periapical status of the focus tooth and neighbors: lesion size, borders, change signs\n2. Canal anatomy: count, configuration, curvature, calcifications, extra-canal signs\n3. Prior treatment quality: fill length, density, voids, missed canals\n4. Resorption, fracture signs, or restorability concerns\n5. Nearby vital structures relevant to treatment (mental foramen, sinus floor)\n6. What to confirm chairside before starting\n\nAnswer in the chat; do not create a patient document.',
  },
  endoPreOpTitle: {
    id: 'agentPrompts.reportEndo.preOpTitle',
    defaultMessage: 'Pre-op briefing',
  },
  endoPreOpPrompt: {
    id: 'agentPrompts.reportEndo.preOpPrompt',
    defaultMessage:
      'Prepare a pre-operative briefing for the tooth in this endodontic report.\n\nOutput:\n1. Tooth and diagnosis context from the report\n2. Anatomy to anticipate: canal count, curvature, calcification\n3. Working-length considerations from the imaging\n4. Risks and access considerations the report raises\n5. Chairside checks to run first (pulp testing, angulated periapical view)\n\nThe imaging is a planning aid; the apex locator and working films remain the chairside truth. Answer in the chat; do not create a patient document.',
  },
  endoRetreatmentTitle: {
    id: 'agentPrompts.reportEndo.retreatmentTitle',
    defaultMessage: 'Retreatment assessment',
  },
  endoRetreatmentPrompt: {
    id: 'agentPrompts.reportEndo.retreatmentPrompt',
    defaultMessage:
      'Assess what this endodontic report shows that is relevant to retreatment versus primary treatment.\n\nOutput:\n1. Existing root canal treatment: present or absent, quality observations\n2. Lesion status and whether it suggests non-healing\n3. Missed or unfilled anatomy the report notes\n4. Vertical root fracture signs — and why they need clinical corroboration near fillings or posts\n5. The options this sets up for the clinician to weigh (retreatment, apical microsurgery, extraction)\n\nPresent the options as flags, not a decision. Answer in the chat; do not create a patient document.',
  },
  implantSitesTitle: {
    id: 'agentPrompts.reportImplant.sitesTitle',
    defaultMessage: 'Site assessment',
  },
  implantSitesPrompt: {
    id: 'agentPrompts.reportImplant.sitesPrompt',
    defaultMessage:
      'Summarize the implant-relevant site assessment in this report.\n\nOutput:\n1. Edentulous or planned sites, by tooth number\n2. Per site: bone height to the nearest vital structure and ridge width\n3. Bone density observations and their limits (CBCT values are relative, not calibrated)\n4. Prosthetic space and adjacent tooth angulation\n5. Ridge defects or grafting flags\n6. What requires a clinical exam before any plan is final\n\nPresent measurements as the report gives them; flag uncertainty. Answer in the chat; do not create a patient document.',
  },
  implantVitalStructuresTitle: {
    id: 'agentPrompts.reportImplant.vitalStructuresTitle',
    defaultMessage: 'Vital structures check',
  },
  implantVitalStructuresPrompt: {
    id: 'agentPrompts.reportImplant.vitalStructuresPrompt',
    defaultMessage:
      'List the vital-structure proximity flags in this implant report.\n\nOutput:\n1. Mandibular canal and inferior alveolar nerve: course and distances per site\n2. Mental foramen position\n3. Maxillary sinus floor and possible sinus-lift flags\n4. Nasal floor and incisive canal where relevant\n5. Which sites the report flags as needing extra caution, and why\n\nFlag proximity — never clear a site as safe; the surgical decision stays with the clinician. Answer in the chat; do not create a patient document.',
  },
  implantPatientDiscussionTitle: {
    id: 'agentPrompts.reportImplant.patientDiscussionTitle',
    defaultMessage: 'Patient discussion prep',
  },
  implantPatientDiscussionPrompt: {
    id: 'agentPrompts.reportImplant.patientDiscussionPrompt',
    defaultMessage:
      'Prepare talking points for discussing implant treatment with this patient.\n\nOutput:\n1. What the imaging shows about the site, in plain words\n2. The treatment steps the report implies (including possible grafting or sinus lift)\n3. Risks to disclose, especially near nerves or sinuses\n4. Alternatives the clinician may present\n5. Open questions only the clinical exam can answer\n\nUse calm, plain language. Do not promise outcomes or finalize the plan; the clinician decides. Answer in the chat; do not create a patient document.',
  },
  implantPlanDocumentTitle: {
    id: 'agentPrompts.reportImplant.planDocumentTitle',
    defaultMessage: 'Implant plan document',
  },
  implantPlanDocumentDetail: {
    id: 'agentPrompts.reportImplant.planDocumentDetail',
    defaultMessage: 'Draft a staged plan as a patient document',
  },
  implantPlanDocumentPrompt: {
    id: 'agentPrompts.reportImplant.planDocumentPrompt',
    defaultMessage:
      'Create and publish a patient document "Implant treatment plan" for this patient, based on this report — not just a chat answer.\n\nWrite the complete patient document Markdown body: the site assessment (bone height, width, and density with dates), vital-structure proximity flags, the proposed staged plan per site, alternatives, and limitations pending clinical exam.\n\nCreate the document with the patient-document workflow, attach the relevant study images, and publish it.\n\nReply in chat with only a concise summary of the created document. Never paste the document body into the chat.',
  },
  molarImpactionTitle: {
    id: 'agentPrompts.reportMolar.impactionTitle',
    defaultMessage: 'Third molar assessment',
  },
  molarImpactionPrompt: {
    id: 'agentPrompts.reportMolar.impactionPrompt',
    defaultMessage:
      'Summarize the third molar assessment in this report.\n\nOutput:\n1. Each third molar, by tooth number: presence and eruption status\n2. Impaction classification the report gives (position and depth, angulation)\n3. Root morphology and maturity\n4. Relationship to the second molar and to the mandibular canal\n5. Associated findings: pericoronitis signs, caries, bone loss, follicular widening\n\nAnswer in the chat; do not create a patient document.',
  },
  molarSurgicalRiskTitle: {
    id: 'agentPrompts.reportMolar.surgicalRiskTitle',
    defaultMessage: 'Surgical risk flags',
  },
  molarSurgicalRiskPrompt: {
    id: 'agentPrompts.reportMolar.surgicalRiskPrompt',
    defaultMessage:
      'List the surgical risk flags for the third molars in this report.\n\nOutput:\n1. Nerve proximity signs per tooth: distance, cortication, canal-through-root signs\n2. Depth and angulation factors that raise difficulty\n3. Age- and root-maturity-related considerations\n4. Findings that typically warrant specialist referral\n5. What the surgeon should confirm with a clinical exam\n\nFlags only — the extraction decision and technique belong to the surgeon. Answer in the chat; do not create a patient document.',
  },
  segmentationOverviewTitle: {
    id: 'agentPrompts.reportSegmentation.overviewTitle',
    defaultMessage: 'Segmentation overview',
  },
  segmentationOverviewPrompt: {
    id: 'agentPrompts.reportSegmentation.overviewPrompt',
    defaultMessage:
      'Summarize what this segmentation report maps.\n\nOutput:\n1. The segmented structures (teeth, jaws, mandibular canal, sinuses, airway, TMJ) and what the 3D model can be used for\n2. Airway observations the report makes, as screening signals only\n3. TMJ and sinus flags\n4. Nerve tracing notes relevant to planned sites\n5. Limits: renderings are aids — decisions come from the slices, and the clinician verifies near metal\n\nAnswer in the chat; do not create a patient document.',
  },
});

/** Generic report page (bare report routes, 2D preview, studio reports). */
export const reportPagePresets: AgentPromptPresetList = {
  write: [c.summaryDocument, c.referralDraft],
  understand: [
    {
      id: 'report.walkthrough',
      group: 'understand',
      title: reportMessages.reportWalkthroughTitle,
      prompt: reportMessages.reportWalkthroughPrompt,
    },
    c.keyFindings,
    c.patientExplanation,
    c.comparePrevious,
  ],
};

export const reportCbctPagePresets: AgentPromptPresetList = {
  write: [c.summaryDocument, c.referralDraft],
  understand: [
    {
      id: 'reportCbct.walkthrough',
      group: 'understand',
      title: reportMessages.cbctWalkthroughTitle,
      prompt: reportMessages.cbctWalkthroughPrompt,
    },
    {
      id: 'reportCbct.incidental',
      group: 'understand',
      title: reportMessages.cbctIncidentalTitle,
      prompt: reportMessages.cbctIncidentalPrompt,
    },
    c.keyFindings,
    c.patientExplanation,
  ],
};

export const reportPanoPagePresets: AgentPromptPresetList = {
  write: [c.followUpDraft, c.referralDraft],
  understand: [
    {
      id: 'reportPano.walkthrough',
      group: 'understand',
      title: reportMessages.panoWalkthroughTitle,
      prompt: reportMessages.panoWalkthroughPrompt,
    },
    {
      id: 'reportPano.thirdMolars',
      group: 'understand',
      title: reportMessages.panoThirdMolarsTitle,
      prompt: reportMessages.panoThirdMolarsPrompt,
    },
    c.patientExplanation,
  ],
};

export const reportBitewingsPagePresets: AgentPromptPresetList = {
  write: [c.followUpDraft],
  understand: [
    {
      id: 'reportBitewings.caries',
      group: 'understand',
      title: reportMessages.bitewingsCariesTitle,
      prompt: reportMessages.bitewingsCariesPrompt,
    },
    {
      id: 'reportBitewings.restorations',
      group: 'understand',
      title: reportMessages.bitewingsRestorationsTitle,
      prompt: reportMessages.bitewingsRestorationsPrompt,
    },
    c.patientExplanation,
  ],
};

export const reportIoxrayPagePresets: AgentPromptPresetList = {
  write: [c.referralDraft],
  understand: [
    {
      id: 'reportIoxray.periapical',
      group: 'understand',
      title: reportMessages.ioxrayPeriapicalTitle,
      prompt: reportMessages.ioxrayPeriapicalPrompt,
    },
    {
      id: 'reportIoxray.endoConcerns',
      group: 'understand',
      title: reportMessages.ioxrayEndoConcernsTitle,
      prompt: reportMessages.ioxrayEndoConcernsPrompt,
    },
    c.patientExplanation,
  ],
};

/** Endodontic CBCT study page. */
export const reportEndoPagePresets: AgentPromptPresetList = {
  write: [c.referralDraft, c.summaryDocument],
  understand: [
    {
      id: 'reportEndo.assessment',
      group: 'understand',
      title: reportMessages.endoAssessmentTitle,
      prompt: reportMessages.endoAssessmentPrompt,
    },
    {
      id: 'reportEndo.preOp',
      group: 'understand',
      title: reportMessages.endoPreOpTitle,
      prompt: reportMessages.endoPreOpPrompt,
    },
    {
      id: 'reportEndo.retreatment',
      group: 'understand',
      title: reportMessages.endoRetreatmentTitle,
      prompt: reportMessages.endoRetreatmentPrompt,
    },
    c.patientExplanation,
  ],
};

/** Implantology CBCT study page. */
export const reportImplantPagePresets: AgentPromptPresetList = {
  write: [
    {
      id: 'reportImplant.planDocument',
      group: 'write',
      title: reportMessages.implantPlanDocumentTitle,
      detail: reportMessages.implantPlanDocumentDetail,
      prompt: reportMessages.implantPlanDocumentPrompt,
    },
    c.referralDraft,
  ],
  understand: [
    {
      id: 'reportImplant.sites',
      group: 'understand',
      title: reportMessages.implantSitesTitle,
      prompt: reportMessages.implantSitesPrompt,
    },
    {
      id: 'reportImplant.vitalStructures',
      group: 'understand',
      title: reportMessages.implantVitalStructuresTitle,
      prompt: reportMessages.implantVitalStructuresPrompt,
    },
    {
      id: 'reportImplant.patientDiscussion',
      group: 'understand',
      title: reportMessages.implantPatientDiscussionTitle,
      prompt: reportMessages.implantPatientDiscussionPrompt,
    },
  ],
};

/** Third molar CBCT study page. */
export const reportMolarPagePresets: AgentPromptPresetList = {
  write: [c.referralDraft],
  understand: [
    {
      id: 'reportMolar.impaction',
      group: 'understand',
      title: reportMessages.molarImpactionTitle,
      prompt: reportMessages.molarImpactionPrompt,
    },
    {
      id: 'reportMolar.surgicalRisk',
      group: 'understand',
      title: reportMessages.molarSurgicalRiskTitle,
      prompt: reportMessages.molarSurgicalRiskPrompt,
    },
    c.patientExplanation,
  ],
};

/** Segmentation / STL page. */
export const reportSegmentationPagePresets: AgentPromptPresetList = {
  write: [c.summaryDocument],
  understand: [
    {
      id: 'reportSegmentation.overview',
      group: 'understand',
      title: reportMessages.segmentationOverviewTitle,
      prompt: reportMessages.segmentationOverviewPrompt,
    },
    c.patientExplanation,
  ],
};
